import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save, Calendar, X, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { resolveImageUrl } from "@/lib/site";
import ImageField from "@/pages/admin/ImageField";

function formatError(detail) {
  if (!detail) return "Något gick fel";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

const EMPTY = {
  title: "",
  description: "",
  date: "",
  time: "",
  image_url: "",
  published: true,
  sort_order: 0,
};

function EventModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial?.id;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Titel krävs");
      return;
    }
    if (!form.date) {
      toast.error("Datum krävs (ÅÅÅÅ-MM-DD)");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description || "",
        date: form.date,
        time: form.time || "",
        image_url: form.image_url || null,
        published: !!form.published,
        sort_order: Number(form.sort_order) || 0,
      };
      if (isEdit) {
        await api.put(`/events/${initial.id}`, payload);
        toast.success("Händelse uppdaterad");
      } else {
        await api.post("/events", payload);
        toast.success("Händelse tillagd");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto">
      <form
        onSubmit={submit}
        data-testid="event-modal"
        className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden my-8"
      >
        <div className="flex items-center justify-between p-6 border-b border-[var(--border)]">
          <h3 className="font-serif-display text-2xl font-bold">
            {isEdit ? "Redigera händelse" : "Ny händelse"}
          </h3>
          <button type="button" onClick={onClose} className="w-9 h-9 grid place-items-center rounded-full hover:bg-[var(--surface-alt)]">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <ImageField
            label="Bild (valfri)"
            value={form.image_url || ""}
            onChange={(v) => update("image_url", v)}
            testId="event-image"
          />
          <label className="block">
            <span className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)]">Titel</span>
            <input
              required
              data-testid="event-title"
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              placeholder="t.ex. Trubadur — Stefan Olofsson"
              className="mt-1 w-full bg-white border border-[var(--border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--primary)]"
            />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)]">Beskrivning</span>
            <textarea
              data-testid="event-description"
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              rows={3}
              placeholder="Kort text som visas på sidan"
              className="mt-1 w-full bg-white border border-[var(--border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--primary)]"
            />
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)]">Datum</span>
              <input
                type="date"
                required
                data-testid="event-date"
                value={form.date || ""}
                onChange={(e) => update("date", e.target.value)}
                className="mt-1 w-full bg-white border border-[var(--border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--primary)]"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)]">Tid</span>
              <input
                type="text"
                data-testid="event-time"
                value={form.time || ""}
                onChange={(e) => update("time", e.target.value)}
                placeholder="19:00"
                className="mt-1 w-full bg-white border border-[var(--border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--primary)]"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)]">Sortering</span>
              <input
                type="number"
                data-testid="event-sort"
                value={form.sort_order}
                onChange={(e) => update("sort_order", e.target.value)}
                className="mt-1 w-full bg-white border border-[var(--border)] rounded-xl px-4 py-3 focus:outline-none focus:border-[var(--primary)]"
              />
            </label>
          </div>
          <label className="inline-flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="event-published"
              checked={!!form.published}
              onChange={(e) => update("published", e.target.checked)}
              className="w-5 h-5 accent-[var(--primary)]"
            />
            <span className="text-sm font-semibold">Publicerad (visas på hemsidan)</span>
          </label>
        </div>
        <div className="flex justify-end gap-2 p-6 bg-[var(--surface-alt)] border-t border-[var(--border)]">
          <button type="button" onClick={onClose} className="rounded-full px-5 py-2.5 font-semibold border border-[var(--border)] bg-white hover:bg-stone-50">
            Avbryt
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="event-save"
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-semibold bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white disabled:opacity-60"
          >
            <Save size={16} /> {saving ? "Sparar…" : "Spara"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function EventsEditor() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | "create" | event object

  const load = async () => {
    try {
      const { data } = await api.get("/admin/events");
      setItems(data || []);
    } catch (err) {
      toast.error("Kunde inte ladda händelser");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (it) => {
    if (!window.confirm(`Ta bort "${it.title}"?`)) return;
    try {
      await api.delete(`/events/${it.id}`);
      toast.success("Borttagen");
      await load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const togglePublished = async (it) => {
    try {
      await api.put(`/events/${it.id}`, { published: !it.published });
      await load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-serif-display text-3xl sm:text-4xl font-black">Kommande aktiviteter</h1>
          <p className="text-[var(--text-muted)] mt-1">
            Trubadurer, livemusik och andra händelser som visas på hemsidan. Endast publicerade händelser i dag eller framåt syns för besökarna.
          </p>
        </div>
        <button
          onClick={() => setModal("create")}
          data-testid="events-add-button"
          className="inline-flex items-center gap-2 rounded-full bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white px-5 py-3 font-semibold shadow-lg shadow-[var(--primary)]/20"
        >
          <Plus size={16} /> Ny händelse
        </button>
      </div>

      <div className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-[var(--text-muted)]">Laddar…</div>
        ) : items.length === 0 ? (
          <div className="py-12 text-center text-[var(--text-muted)]">
            Inga händelser ännu. Lägg till din första aktivitet ovan.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[var(--surface-alt)] text-xs uppercase tracking-wider text-[var(--text-muted)]">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Händelse</th>
                <th className="text-left px-5 py-3 font-semibold hidden sm:table-cell">Datum</th>
                <th className="text-left px-5 py-3 font-semibold hidden md:table-cell">Tid</th>
                <th className="text-center px-5 py-3 font-semibold">Status</th>
                <th className="text-right px-5 py-3 font-semibold">Åtgärder</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)]">
              {items.map((it) => {
                const isPast = it.date && it.date < today;
                return (
                  <tr key={it.id} data-testid={`event-row-${it.id}`} className={isPast ? "opacity-60" : ""}>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 shrink-0 rounded-lg overflow-hidden bg-[var(--surface-alt)] border border-[var(--border)] grid place-items-center">
                          {it.image_url ? (
                            <img src={resolveImageUrl(it.image_url)} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Calendar size={16} className="text-[var(--text-muted)]" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold truncate">{it.title}</div>
                          {it.description && (
                            <div className="text-xs text-[var(--text-muted)] mt-0.5 line-clamp-1">{it.description}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 hidden sm:table-cell text-[var(--text-muted)] tabular-nums">{it.date}</td>
                    <td className="px-5 py-4 hidden md:table-cell text-[var(--text-muted)]">{it.time || "—"}</td>
                    <td className="px-5 py-4 text-center">
                      <button
                        onClick={() => togglePublished(it)}
                        data-testid={`event-toggle-${it.id}`}
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
                          it.published
                            ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                            : "bg-stone-100 text-stone-500 hover:bg-stone-200"
                        }`}
                      >
                        {it.published ? <Eye size={12} /> : <EyeOff size={12} />}
                        {it.published ? "Publicerad" : "Dold"}
                      </button>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex gap-1 justify-end">
                        <button
                          onClick={() => setModal(it)}
                          data-testid={`event-edit-${it.id}`}
                          className="rounded-full px-3 py-1.5 text-xs font-semibold bg-white border border-[var(--border)] hover:border-[var(--text)]"
                        >
                          Redigera
                        </button>
                        <button
                          onClick={() => remove(it)}
                          data-testid={`event-delete-${it.id}`}
                          className="w-8 h-8 grid place-items-center rounded-full hover:bg-red-50 hover:text-red-600"
                          title="Ta bort"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <EventModal
          initial={modal === "create" ? null : modal}
          onClose={() => setModal(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
